import React, { Fragment, useEffect, useState, useRef, useMemo } from 'react';
import styles from './styles.scss';
import { Icons, Button } from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';

import Mousetrap from 'mousetrap';
import {updateQueryStringKey} from '../../../utils/rx-store-serialization';
import { ON_PREM } from '../../../utils/environment';

import {
  coordinateToDisplay,
  displayToCoordinate,

  TICK_BAR_HEIGHT,
  TRACK_HEIGHT,
  BOTTOM_MARGIN_HEIGHT,
  CAPTURE_FRAMERATE_FPS,
} from './constants';
import TrackTimelineEvents from './track-timeline-events';

import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';
import MPIEventTimelineStore from '../../../rx-stores/event-timeline';
import { EventTimelineActionTypes } from '../../../types/event-timeline';
import { CaptureStatus } from '../../../types/api';

import { SenseiApi } from '../../../utils/api';

type TimelineTag = {
  id: any;
  secondsOffset: number;
  tag: {
    id: number;
    name: string;
    created_at: string;
    start_frame: number;
  };
};


export type Track = {
  serialNumber: string;
  captureStatus: CaptureStatus;
  primary: boolean;
  offsetInSeconds: number;
  videoRef: HTMLVideoElement;
  annotationEvents: Array<RenderedTimelineEvent>;
  algoEvents: Array<RenderedTimelineEvent>;
};

// Convert from "track local time" to "seek time"
function getSeek(track: Track): number {
  return track.videoRef.currentTime - track.offsetInSeconds;
}

// Convert from "seek time" to "track local time"
function setFromSeek(track: Track, seek) {
  track.videoRef.currentTime = seek + track.offsetInSeconds;
}

export type TimelineEvent = {
  id: any;
  secondsOffset: number;
  direction: 1 | -1;
  filled: boolean;
  x: number;
  y: number;
};

export type InvisibleTimelineEvent = TimelineEvent & {
  visibleInTrack: false;
}

export type VisibleTimelineEvent = TimelineEvent & {
  visibleInTrack?: true;
  label?: number;
}

export type RenderedTimelineEvent = InvisibleTimelineEvent | VisibleTimelineEvent;

export function convertToTimelineEvent(input: Array<any>): Array<TimelineEvent> {
  return input.map(i => ({
    ...i,
    secondsOffset: parseFloat(i.seconds_offset),
    filled: !i.is_dupe,
    x: i.x_position,
    y: i.y_position,
  }));
}


export type PlaybackState = {
  seek: number;
  bufferedRanges: Array<[number, number]>;
};

type ViewerPlaybackControlsProps = {
  playbackState: PlaybackState; onSetPlaybackState: (p: PlaybackState) => void;

  tags?: Array<TimelineTag>;
  tracks: Array<Track>;
  trackLengthInSeconds?: number,
  hideFineTimeline?: boolean;

  keyboardShortcutsEnabled: boolean;

  // Run when the user lifts their mouse after a seek operation has completed
  onSeekFinished?: (timeInSeconds: number) => void;
};

export const ViewerPlaybackControls: React.FunctionComponent<ViewerPlaybackControlsProps> = ({
  playbackState,
  onSetPlaybackState,
  keyboardShortcutsEnabled,
  hideFineTimeline,

  tags = [],
  tracks,
  trackLengthInSeconds,
  onSeekFinished = () => null,
}) => {
  const { seek, bufferedRanges } = playbackState;

  const primary = tracks.find(u => u.primary);
  const mirrored = tracks.filter(u => !u.primary);

  const [playing, setPlaying] = useState(false);

  const loopInterval = useRef(null);

  const playPauseButtonRef = useRef(null);

  const state = useRxStore(MPIEventTimelineStore);
  const dispatch = useRxDispatch();

  useEffect(() => {
    let running = true;
    function frame() {
      let seek = getSeek(primary);

      // Check to see if we've reached the end of the track
      // HACK - trackLengthInSeconds isnt computed until the ref is assigned, so we make a shortcut
      // to ignore it on the first render. Becomes useful after first render for setting seeks after
      // first position is assigned
      if (trackLengthInSeconds > 0 && seek >= trackLengthInSeconds) {
        tracks.forEach(u => u.videoRef.pause());
        onPause();

        seek = trackLengthInSeconds;
        tracks.forEach(track => {
          setFromSeek(track, seek);
        });
      }

      onSetPlaybackState({
        seek,
        bufferedRanges: Array(primary.videoRef.buffered.length)
          .fill(0)
          .map((d, i) => [
            primary.videoRef.buffered.start(i),
            primary.videoRef.buffered.end(i),
          ]) as Array<[number, number]>,
      });


      if (running) {
        loopInterval.current = window.requestAnimationFrame(frame);
      }
    }

    if (primary.videoRef) {
      if (primary.videoRef.paused) {
        onPause();
      } else {
        onPlay();
      }

      // Update all videos
      tracks.forEach(track => {
        setFromSeek(track, seek);
      });

      primary.videoRef.addEventListener('play', onPlay);
      primary.videoRef.addEventListener('pause', onPause);
      loopInterval.current = window.requestAnimationFrame(frame);

      return () => {
        primary.videoRef.removeEventListener('play', onPlay);
        primary.videoRef.removeEventListener('pause', onPause);
        running = false;
        window.cancelAnimationFrame(loopInterval.current);
      };
    }
  }, [trackLengthInSeconds, primary.videoRef, primary.serialNumber, primary.offsetInSeconds]);

  function onPlay() {
    setPlaying(true);
  }

  function onPause() {
    setPlaying(false);
    onSeekFinished(getSeek(primary));
  }

  useEffect(() => {
    if (!keyboardShortcutsEnabled) {
      return;
    }

    const mousetrap = new Mousetrap(document);

    // Space: toggle play / pause
    mousetrap.bind(['space'], e => {
      e.preventDefault();
      if (playing) {
        tracks.forEach(u => u.videoRef.pause());
        onPause();
      } else {
        tracks.forEach(u => u.videoRef.play());
        onPlay();
      }

      // After pressing space, focus the play/pause button
      // Otherwise, repeated pressing of the space key may not be processed correctly
      // by moustrap fpr an unknown reason.
      if (playPauseButtonRef.current) {
        playPauseButtonRef.current.focus();
      }
    });

    // Experimenting with some davinci resolve-esque shortcuts for scrubbing back and forth
    mousetrap.bind(['j'], e => {
      e.preventDefault();
      onSeek(seek - 3 / CAPTURE_FRAMERATE_FPS);
    });
    mousetrap.bind(['l'], e => {
      e.preventDefault();
      onSeek(seek + 3 / CAPTURE_FRAMERATE_FPS);
    });

    // Left and Right: move back and forth by frame,
    // or if holding shift, by seconds
    mousetrap.bind(['left', 'shift+left'], e => {
      e.preventDefault();
      const amount = e.shiftKey ? 30 : 1;
      onSeek(seek - amount / CAPTURE_FRAMERATE_FPS);
    });
    mousetrap.bind(['right', 'shift+right'], e => {
      e.preventDefault();
      const amount = e.shiftKey ? 30 : 1;
      onSeek(seek + amount / CAPTURE_FRAMERATE_FPS);
    });

    return () => mousetrap.reset();
  }, [playing, seek, keyboardShortcutsEnabled]);

  if (!primary.videoRef) {
    return null;
  }

  const onSeek = seek => {
    updateQueryStringKey("t", seek);
    onSetPlaybackState({ bufferedRanges, seek });
    tracks.forEach(track => setFromSeek(track, seek));
  };

  const onSetPlaying = playing => {
    setPlaying(playing);
    tracks.forEach(track => playing ? track.videoRef.play() : track.videoRef.pause());
  };


  return (
    <Fragment>
     {Boolean(state.pendingChanges.length) &&
      <div className={styles.repositionEventNotice}>
        <div className={styles.repositionEventNoticeTitle}>Unsaved Changes</div>

        <div>You have unsaved changes:
          <ul>
            {state.pendingChanges.map(change => (
              <li key={change.id}>Event {change.id} moved to {change.secondsInTrack}s</li>
            )
            )}
          </ul>
        </div>

        <div className={styles.repositionEventNoticeButton}>
          <Button
            variant="filled"
            onClick={() => {
              dispatch({
                type:  EventTimelineActionTypes.EVENT_TIMELINE_UPDATE_ANNOTATION_EVENTS,
                changes: state.pendingChanges,
              })
            }}
          >
            Save Changes
          </Button>
        </div>
      </div>
      }
      <CourseTimeline
        primary={primary}
        mirrored={mirrored}
        trackLengthInSeconds={trackLengthInSeconds}

        seek={seek}
        onSeek={onSeek}
        onSeekFinished={onSeekFinished}

        bufferedRanges={bufferedRanges}
        playing={playing}
        onSetPlaying={onSetPlaying}
        playPauseButtonRef={playPauseButtonRef}
      />
      {hideFineTimeline ? null : <FineTimeline
        primary={primary}
        mirrored={mirrored}
        trackLengthInSeconds={trackLengthInSeconds}

        seek={seek}
        onSeek={onSeek}
        onSeekFinished={onSeekFinished}
        tracks={tracks}
        tags={tags}
        onSetPlaying={onSetPlaying}
      />}
    </Fragment>
  );
};

const FineTimeline: React.FunctionComponent<{
  primary: Track;
  mirrored: Array<Track>;
  trackLengthInSeconds: number;

  seek: number;
  onSeek: (seek: number) => void; // Run whenever the seek position is changed
  onSeekFinished?: (seek: number) => void; // Run when the mouse is lifted

  tags: Array<TimelineTag>;
  tracks: Array<Track>;
  onSetPlaying: (playing: boolean) => void,
}> = ({
  primary,
  mirrored,
  trackLengthInSeconds,
  seek,
  onSeek,
  onSeekFinished,
  tracks,
  tags,
  onSetPlaying,
}) => {
  const [mouseDown, setMouseDown] = useState(false);
  const timeline = useRef<SVGSVGElement>(null);

  const initialPress = useRef<number>(null);

  const width = coordinateToDisplay(trackLengthInSeconds);
  const height = TICK_BAR_HEIGHT + (tracks.length * TRACK_HEIGHT) + BOTTOM_MARGIN_HEIGHT;

  const ticks = useMemo(() => {
    let ticks = [];
    for (let i = 0; i < trackLengthInSeconds; i += 2) {
      ticks.push(
        <text
          key={i}
          transform={`translate(${coordinateToDisplay(i)},12)`}
          textAnchor="middle"
          alignmentBaseline="middle"
          fontSize={12}
          style={{ userSelect: 'none' }}
        >
          {i}
        </text>,
      );
    }

    return ticks;
  }, [trackLengthInSeconds]);

  const rules = useMemo(() => {
    let rules = [];
    for (let i = 0; i < trackLengthInSeconds; i += 1) {
      const x = coordinateToDisplay(i);
      rules.push(
        <line
          key={i}
          x1={x}
          y1={TICK_BAR_HEIGHT}
          x2={x}
          y2={height}
          stroke={colorVariables.gray200}
          style={{ pointerEvents: 'none' }} // Disallow mouse focus on rules
        />,
      );
    }

    return rules;
  }, [trackLengthInSeconds]);

  return (
    <div className={styles.fineTimelineWrapper}>
      <div className={styles.serialNumberColumn} style={{paddingBottom: BOTTOM_MARGIN_HEIGHT}}>
        {tracks.map(unit => (
          <div
            key={unit.serialNumber}
            className={styles.serialNumberColumnItem}
            style={{height: TRACK_HEIGHT}}
          >
            {unit.serialNumber}
          </div>
        ))}
      </div>

      <svg
        className={styles.fineTimeline}
        ref={timeline}
        onMouseDown={e => {
          if (e.buttons !== 1) {
            return;
          }
          if (typeof timeline.current === 'undefined') {
            return;
          }

          setMouseDown(true);

          onSetPlaying(false);

          initialPress.current =
            e.clientX - timeline.current.getBoundingClientRect().left;

          function move(e) {
            if (e.buttons !== 1) {
              return;
            }

            const mouseX =
              e.clientX - timeline.current.getBoundingClientRect().left;
            const delta = initialPress.current - mouseX;

            let newPosition = displayToCoordinate(
              coordinateToDisplay(seek) + delta,
            );
            if (newPosition < 0) {
              newPosition = 0;
            }
            if (newPosition > trackLengthInSeconds) {
              newPosition = trackLengthInSeconds;
            }
            onSeek(newPosition);
          }

          window.addEventListener('mousemove', move);

          function mouseUp(e) {
            setMouseDown(false);
            window.removeEventListener('mouseup', mouseUp);
            window.removeEventListener('mousemove', move);
            initialPress.current = null;

            onSeek(getSeek(primary));
            if (onSeekFinished) {
              onSeekFinished(getSeek(primary));
            }
          }

          window.addEventListener('mouseup', mouseUp);
        }}
        style={{ cursor: mouseDown ? 'grabbing' : 'grab', height }}
        data-test-id="capture-viewer-playback-controls-fine-timeline"
      >
        {/* Line that seperates the ticks across the top of the screen from the tracks */}
        <line
          x1={0}
          y1={TICK_BAR_HEIGHT}
          x2="100%"
          y2={TICK_BAR_HEIGHT}
          stroke={colorVariables.gray400}
        />

        {/* Render the background for each track - this is non-horizontally-moving */}
        {tracks.map((unit, unitIndex) => {
          const yPosition = TICK_BAR_HEIGHT + unitIndex * TRACK_HEIGHT;
          const annotationEventsYPosition = yPosition;
          const algoEventsYPosition = yPosition + TRACK_HEIGHT / 2;
          const algoEventsFill = ON_PREM ? "#FFF" : colorVariables.gray100;

          return (
            <Fragment key={unit.serialNumber}>
              {!ON_PREM ?
              <rect
                x={0}
                y={annotationEventsYPosition}
                width="100%"
                height={TRACK_HEIGHT / 2}
                fill="#fff"
                style={{ pointerEvents: 'none' }} // Disallow mouse focus on background
              />:null}
              <rect
                x={0}
                y={algoEventsYPosition}
                width="100%"
                height={TRACK_HEIGHT / 2}
                fill={algoEventsFill}
                style={{ pointerEvents: 'none' }} // Disallow mouse focus on background
              />
              <line
                x1={0}
                y1={yPosition + TRACK_HEIGHT}
                x2="100%"
                y2={yPosition + TRACK_HEIGHT}
                stroke={colorVariables.gray300}
              />
            </Fragment>
          );
        })}

        {/* The horizontally moving playback */}
        <svg x="50%" y="0" overflow="visible">
          <g transform={`translate(${-1 * coordinateToDisplay(seek)},0)`}>
            {ticks}
            {rules}

            {tags.map(tag => (
              <path
                d="M10.6516504,18.429825 L18.429825,10.6516504 C18.7227182,10.3587572 18.7227182,9.88388348 18.429825,9.59099026 L9.94454365,1.10570888 C9.79512581,0.956291047 9.58960926,0.876836935 9.3785397,0.886887866 L1.9539185,1.24044126 C1.56774921,1.25883027 1.25883027,1.56774921 1.24044126,1.9539185 L0.886887866,9.3785397 C0.876836935,9.58960926 0.956291047,9.79512581 1.10570888,9.94454365 L9.59099026,18.429825 C9.88388348,18.7227182 10.3587572,18.7227182 10.6516504,18.429825 Z M2.40097111,9.11848554 L2.70631268,2.70631268 L9.11848554,2.40097111 L16.8388348,10.1213203 L10.1213203,16.8388348 L2.40097111,9.11848554 Z M9.23743687,9.23743687 C10.3113787,8.16349506 10.3113787,6.42229137 9.23743687,5.34834957 C8.16349506,4.27440777 6.42229137,4.27440777 5.34834957,5.34834957 C4.27440777,6.42229137 4.27440777,8.16349506 5.34834957,9.23743687 C6.42229137,10.3113787 8.16349506,10.3113787 9.23743687,9.23743687 Z M8.1767767,8.1767767 C7.68862133,8.66493206 6.89716511,8.66493206 6.40900974,8.1767767 C5.92085438,7.68862133 5.92085438,6.89716511 6.40900974,6.40900974 C6.89716511,5.92085438 7.68862133,5.92085438 8.1767767,6.40900974 C8.66493206,6.89716511 8.66493206,7.68862133 8.1767767,8.1767767 Z"
                fill={colorVariables.reportYellow}
                key={`${tag.id},${tag.secondsOffset}`}
                transform={`translate(${coordinateToDisplay(tag.secondsOffset) -
                  6},18) scale(0.5)`}
              />
            ))}

            {tracks.map((track, trackIndex) => (
              // Render data on each track timeline
              <TrackTimelineEvents
                key={track.serialNumber}

                track={track}
                trackIndex={trackIndex}
                trackLengthInSeconds={trackLengthInSeconds}

                seek={seek}
                onSeek={onSeek}
              />
            ))}
          </g>
        </svg>

        {/* Render the 'ANNOTATIONS' or 'ALGO' text for each track - this is non-horizontally-moving */}
        {tracks.map((unit, unitIndex) => {
          const yPosition = TICK_BAR_HEIGHT + unitIndex * TRACK_HEIGHT;
          const annotationEventsYPosition = yPosition;
          const algoEventsYPosition = ON_PREM ? annotationEventsYPosition : yPosition + TRACK_HEIGHT / 2;
          return (
            <Fragment key={unit.serialNumber}>
              {!ON_PREM ?
                <text
                  fill={colorVariables.gray400}
                  transform={`translate(4,${annotationEventsYPosition + 16})`}
                  fontSize={12}
                  fontWeight="bold"
                  style={{ userSelect: 'none' }}
                >
                  ANNOTATIONS ({unit.annotationEvents.length})
                </text> : null}
                <text
                  fill={colorVariables.gray400}
                  transform={`translate(4,${algoEventsYPosition + 16})`}
                  fontSize={12}
                  fontWeight="bold"
                  style={{ userSelect: 'none' }}
                >
                  ALGO ({unit.algoEvents.length})
                </text>
            </Fragment>
          );
        })}

        {/* Pointer */}
        <line
          x1="50%"
          y1={0}
          x2="50%"
          y2={height}
          stroke={colorVariables.red}
          style={{ userSelect: 'none' }}
        />
      </svg>
    </div>
  );
};

const CourseTimeline: React.FunctionComponent<{
  primary: Track;
  mirrored: Array<Track>;
  trackLengthInSeconds: number;

  seek: number;
  onSeek: (seek: number) => void; // Run whenever the seek position is changed
  onSeekFinished?: (seek: number) => void; // Run when the mouse is lifted

  bufferedRanges: Array<[number, number]>;
  playing: boolean;
  onSetPlaying: (playing: boolean) => void;
  playPauseButtonRef: React.Ref<HTMLButtonElement>;
}> = ({
  primary,
  mirrored,
  trackLengthInSeconds,
  seek,
  onSeek,
  onSeekFinished,
  bufferedRanges,
  playing,
  onSetPlaying,
  playPauseButtonRef,
}) => {
  const timeline = useRef<HTMLDivElement>(null);

  const seekPercent = (seek / trackLengthInSeconds) * 100;

  function dragTimeline(e) {
    if (e.buttons !== 1) {
      return;
    }
    if (typeof timeline.current === 'undefined') {
      return;
    }

    const leftPixels = e.clientX - timeline.current.offsetLeft;
    let percent = leftPixels / timeline.current.offsetWidth;
    if (percent > 1) {
      percent = 1;
    }
    if (percent < 0) {
      percent = 0;
    }

    const timeInSeconds = percent * trackLengthInSeconds;
    onSeek(timeInSeconds);
  }

  return (
    <div className={styles.courseTimelineWrapper}>
      {playing ? (
        <button
          className={styles.controlButton}
          onClick={(e) => {
            e.preventDefault();
            onSetPlaying(false)
          }}
          data-test-id="capture-viewer-playback-controls-pause"
          ref={playPauseButtonRef}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 512 512"
          >
            <path
              fill="#fff"
              d="M162.642 148.337h56.034v215.317h-56.034V148.338zM293.356 148.337h56.002v215.317h-56.002V148.338z"
            ></path>
          </svg>
        </button>
      ) : (
        <button
          className={styles.controlButton}
          onClick={(e) => {
            e.preventDefault();
            onSetPlaying(true)
          }}
          data-test-id="capture-viewer-playback-controls-play"
          ref={playPauseButtonRef}
        >
          <Icons.PlaybackPlay width={28} height={28} color="#fff" />
        </button>
      )}
      <div
        ref={timeline}
        className={styles.courseTimeline}
        data-test-id="capture-viewer-playback-controls-course-timeline"
        onMouseDown={e => {
          dragTimeline(e);

          window.addEventListener('mousemove', dragTimeline);

          function mouseUp(e) {
            window.removeEventListener('mouseup', mouseUp);
            window.removeEventListener('mousemove', dragTimeline);
            dragTimeline(e);
            if (onSeekFinished) {
              onSeekFinished(getSeek(primary));
            }
          }

          window.addEventListener('mouseup', mouseUp);
        }}
      >
        <div
          className={styles.courseTimelineInner}
          style={{ width: `${seekPercent}%` }}
        >
          <div className={styles.courseTimelineInnerHandle} />
        </div>
        {bufferedRanges.map(([start, end]) => (
          <div
            key={`${start},${end}`}
            className={styles.courseTimelineBufferedRange}
            style={{
              left: `${(start / trackLengthInSeconds) * 100}%`,
              width: `${((end - start) / trackLengthInSeconds) * 100}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
};
